import { useEffect, useState, useRef } from "react";
import api, { apiErr, fmtDate, API_BASE } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, FolderOpen, Trash2, FileText, ExternalLink, Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LINK_TYPES = ["prospect", "opportunité", "devis", "contrat", "projet", "tâche", "collaborateur"];

export default function Documents() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ nom: "", type_fichier: "PDF", lien_type: "projet", lien_name: "", url: "", taille: "" });
  const [uploading, setUploading] = useState(false);
  const [dl, setDl] = useState("");
  const [thumbs, setThumbs] = useState({});
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();
  const uploadMeta = useRef({ lien_type: "projet", lien_name: "" });

  const load = () => api.get("/documents").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    (items || []).forEach((d) => {
      if (d.type_fichier === "Image" && d.storage_path && !thumbs[d.id]) {
        api.get(`/documents/${d.id}/download`, { responseType: "blob" })
          .then((res) => setThumbs((t) => ({ ...t, [d.id]: URL.createObjectURL(res.data) })))
          .catch(() => {});
      }
    });
  }, [items]);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/documents", f); toast.success("Document ajouté"); setOpen(false); setF({ nom: "", type_fichier: "PDF", lien_type: "projet", lien_name: "", url: "", taille: "" }); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/documents/${id}`); load(); };

  const onPickFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("lien_type", uploadMeta.current.lien_type);
    fd.append("lien_name", uploadMeta.current.lien_name);
    try { await api.post("/documents/upload", fd); toast.success("Fichier téléversé"); load(); }
    catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (d) => {
    setDl(d.id);
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.nom; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(apiErr(e)); } finally { setDl(""); }
  };

  return (
    <div>
      <PageHeader title="Documents" subtitle="Bibliothèque documentaire — téléversez plans, PDF, Word, Excel et images">
        <input ref={fileRef} type="file" hidden onChange={onPickFile}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.csv,.txt" />
        <Button variant="outline" onClick={() => setOpen(true)} data-testid="add-document"><Plus className="h-4 w-4 mr-1.5" />Lien externe</Button>
        <Button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="upload-document" className="bg-brand-600 hover:bg-brand-700 text-white">
          {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Téléverser un fichier
        </Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Aucun document" subtitle="Téléversez vos fichiers réels (plans, PDF, images) ou ajoutez un lien externe." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Nom</TableHead><TableHead>Type</TableHead><TableHead>Taille</TableHead><TableHead>Associé à</TableHead><TableHead>Ajouté par</TableHead><TableHead>Date</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} className="hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                  <TableCell className="font-semibold">
                    <div className="flex items-center gap-2">
                      {thumbs[d.id] ? (
                        <img src={thumbs[d.id]} alt={d.nom} onClick={() => setPreview(d)}
                          data-testid={`doc-thumb-${d.id}`}
                          className="h-10 w-10 rounded object-cover border cursor-pointer hover:ring-2 hover:ring-brand-400" />
                      ) : (
                        <FileText className="h-4 w-4 text-brand-500" />
                      )}
                      <span>{d.nom}</span>
                    </div>
                  </TableCell>
                  <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{d.type_fichier}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{d.taille || "—"}</TableCell>
                  <TableCell className="text-sm capitalize">{d.lien_type} {d.lien_name && `· ${d.lien_name}`}</TableCell>
                  <TableCell className="text-sm text-slate-500">{d.uploaded_by}</TableCell>
                  <TableCell className="text-sm">{fmtDate(d.created_at)}</TableCell>
                  <TableCell className="flex items-center gap-1">
                    {d.storage_path ? (
                      <Button variant="ghost" size="icon" onClick={() => download(d)} disabled={dl === d.id} data-testid={`doc-download-${d.id}`}>
                        {dl === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      </Button>
                    ) : d.url ? (
                      <a href={d.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon"><ExternalLink className="h-4 w-4" /></Button></a>
                    ) : null}
                    <Button variant="ghost" size="icon" onClick={() => remove(d.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Ajouter un lien externe</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label className="text-xs">Nom du document</Label><Input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} required className="mt-1" data-testid="doc-nom" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type de fichier</Label>
                <Select value={f.type_fichier} onValueChange={(v) => setF({ ...f, type_fichier: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["PDF", "Word", "Excel", "Image", "Autre"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Associé à</Label>
                <Select value={f.lien_type} onValueChange={(v) => setF({ ...f, lien_type: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{LINK_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label className="text-xs">Nom de l&apos;élément associé</Label><Input value={f.lien_name} onChange={(e) => setF({ ...f, lien_name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Lien / URL</Label><Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className="mt-1" placeholder="https://" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" className="bg-brand-600 text-white" data-testid="doc-submit">Ajouter</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-head">{preview?.nom}</DialogTitle></DialogHeader>
          {preview && thumbs[preview.id] && (
            <img src={thumbs[preview.id]} alt={preview.nom} data-testid="doc-preview-img"
              className="w-full max-h-[70vh] object-contain rounded-lg border" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
